import { LOCALE_NAMES, parseDbTimestamp } from "@repo/shared";
import type { LocaleCode } from "@repo/shared/types";
import { Copy, Trash2 } from "lucide-react";
import { CloneJobRow } from "@/components/clone-jobs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/hooks/use-confirm";
import { useListedCloneJobs } from "@/lib/hooks/use-clone-jobs";
import { useDeletePdfTemplate } from "@/lib/hooks/use-pdf-templates";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import type { PdfTemplateListItem } from "@/lib/types/pdf-template";
import { T } from "./pdf-templates-table.i18n";

type PdfTemplatesTableProps = {
  templates: PdfTemplateListItem[];
  onClone: (template: PdfTemplateListItem) => void;
};

export function PdfTemplatesTable({ templates, onClone }: PdfTemplatesTableProps) {
  const deleteMutation = useDeletePdfTemplate();
  const { confirm, ConfirmationDialog } = useConfirm();
  const t = useT(T);
  const { formatDate } = useRegionalFormat();
  // QL-334 — translated copies still being made; a finished one is a real row.
  const cloneJobs = useListedCloneJobs("pdf_template");

  const handleDelete = async (id: string, name: string) => {
    const confirmed = await confirm({
      title: t.deletePdfTemplate,
      description: t.deleteConfirmation(name),
      confirmText: t.delete,
      cancelText: t.cancel,
      variant: "destructive",
    });

    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(id);
    } catch (err) {
      await confirm({
        title: t.error,
        description: err instanceof Error ? err.message : t.failedToDelete,
        confirmText: t.ok,
        variant: "destructive",
        type: "alert",
      });
    }
  };

  if (templates.length === 0 && cloneJobs.length === 0) {
    return <EmptyState>{t.noTemplatesYet}</EmptyState>;
  }

  return (
    <>
      <Table variant="navigation">
        <TableHeader>
          <TableRow>
            <TableHead>{t.name}</TableHead>
            <TableHead className="w-28">{t.language}</TableHead>
            <TableHead>{t.flow}</TableHead>
            <TableHead>{t.status}</TableHead>
            <TableHead>{t.created}</TableHead>
            <TableActionsHead>{t.actions}</TableActionsHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cloneJobs.map((job) => (
            <CloneJobRow key={job.id} job={job} colSpan={6} />
          ))}
          {templates.map((template) => (
            <TableRow key={template.id}>
              <TableCellLink className="font-medium" to={`/settings/pdf-templates/${template.id}`}>
                {template.name}
              </TableCellLink>
              <TableCell className="text-sm text-muted-foreground">
                {template.language
                  ? LOCALE_NAMES[template.language as LocaleCode] || template.language
                  : "-"}
              </TableCell>
              <TableCell>{template.flow?.name || "-"}</TableCell>
              <TableCell>
                {template.isActive ? (
                  <Badge variant="default">{t.active}</Badge>
                ) : (
                  <Badge variant="secondary">{t.inactive}</Badge>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {formatDate(parseDbTimestamp(template.createdAt))}
              </TableCell>
              <TableActionsCell>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onClone(template)}
                  tooltip={t.clone}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(template.id, template.name)}
                  disabled={deleteMutation.isPending}
                  tooltip={t.delete}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableActionsCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ConfirmationDialog />
    </>
  );
}
